import { remove } from '../helpers/array';
import { hasProp, toType } from '../helpers/def';
import { div, tag } from '../helpers/html';
import { getLang } from '../helpers/localization';
import { Types } from './Enums';
import { GS } from './GS.js';
import { RuntimeGS } from './app/RuntimeGS';
import {
    getTemplateConstructor,
    registerTemplateConstructor,
    templateMetadataGS,
    tryGetTemplateElement,
    unregisterTemplateConstructor
} from './template/TemplateRegistryGS';

interface WrapperConstructor { new(...args: any[]): WrapperGS }
interface WrapperTagClassedConstructor { new(...args: any[]): WrapperGS; readonly TagClassName: string }
interface WrapperHTMLElementedConstructor { new(...args: any[]): WrapperGS; ForHTMLElement(el: HTMLElement): boolean }
interface WrapperDelegate { (wr: WrapperGS): void }

function tryGetTemplateWrapper(name: string): WrapperGS<HTMLElement> | undefined {
    const element = tryGetTemplateElement(name, WrapperGS.ClassNameTemplate);
    return element ? WrapperGS.Wrap(element) as WrapperGS<HTMLElement> : undefined;
}

function getTemplateWrapper(name: string): WrapperGS<HTMLElement> {
    const wrapper = tryGetTemplateWrapper(name);
    if (!wrapper)
        throw new Error(`Cannot find GS template '${name}'. Ensure its template pack is loaded.`);
    return wrapper;
}

export enum WrapperInitModeGSEnum {
    Wrap,
    CloneElement,
    Adopt
}

/**Used for getting and setting data in FormsGS */
export interface Valued { value: any }
/**Used for getting and setting data in FormsGS */
export interface WrapperValued extends WrapperGS, Valued {}
/**Used for getting and setting data in FormsGS */
export interface WrapperAsyncValued extends WrapperGS {
    setValueAsync(value: any): Promise<any>;
    getValueAsync(): Promise<any>;
}
export interface WrapperAsyncValidable extends WrapperGS {
    validAsync(rule: string, ruleArgs: string): Promise<string | boolean>;
}
export interface WrapperValidable extends WrapperGS {
    valid(rule: string, ruleArgs: string): string | boolean;
}
export class WrapperGS<T extends HTMLElement = HTMLElement> 
{
    private static readonly _htmlNamespace = "http://www.w3.org/1999/xhtml";

    public static ClassNameTemplate = "tmpl";

    public static AttrGSTargetShow = "target-show";
    public static AttrGSTargetActive = "target-active";

    public static readonly ClassNameHide = "d-none";
    public static readonly ClassNameActive = "active";
    public static readonly ClassNameDisabled = "disabled";

    public static ToValued(wr: WrapperGS): WrapperValued | undefined {
        if (!hasProp(wr, "value", Types.NotFunction)) return undefined;
        return wr as WrapperValued;            
    }
    public static ToAsyncValued(wr: WrapperGS): WrapperAsyncValued | undefined {
        if (!hasProp(wr, "setValueAsync", Types.Function)) return undefined;
        if (!hasProp(wr, "getValueAsync", Types.Function)) return undefined;
        return wr as WrapperAsyncValued;
    }
    public static ToValidable(wr: WrapperGS): WrapperValidable | undefined {
        if (!hasProp(wr, "valid", Types.Function)) return undefined;
        return wr as WrapperValidable;
    }
    public static ToAsyncValidable(wr: WrapperGS): WrapperAsyncValidable | undefined {
        if (!hasProp(wr, "validAsync", Types.Function)) return undefined;
        return wr as WrapperAsyncValidable;
    }

   

    public static InjectComponents(component: WrapperConstructor, fullNameForTmplFile: string): void {
        registerTemplateConstructor(fullNameForTmplFile, component);
    }
    public static ReInjectComponents(fullNameForTmplFile: string): void {
        unregisterTemplateConstructor(fullNameForTmplFile);
    }
    public static GetInjectedComponentConstructor(fullNameForTmplFile: string): WrapperConstructor | undefined {
        return getTemplateConstructor<WrapperGS>(fullNameForTmplFile) as WrapperConstructor | undefined;
    }

    private static _ExtendComponentsByTagClass: Map<string, WrapperTagClassedConstructor> = new Map<string, WrapperTagClassedConstructor>();
    public static ExtendComponentsByTagClass(component: WrapperTagClassedConstructor) {
        const lib = WrapperGS._ExtendComponentsByTagClass;
        if (!lib.has(component.TagClassName)){lib.set(component.TagClassName, component); return;}
        if (lib.get(component.TagClassName)!=component)
            throw new Error(`Component TagClass '${component.TagClassName}' is duplicated.`);
    }

    /**Return all tagClasses which extends components */
    public static GetExtendComponentsTagClasses(): string[] { return [...WrapperGS._ExtendComponentsByTagClass.keys()]; }     
    private static _ExtendComponentsByHTMLElement: WrapperHTMLElementedConstructor[] = [];
    public static ExtendComponentsByHTMLElement(...components: WrapperHTMLElementedConstructor[]) {
        for (const component of components)
            if (!WrapperGS._ExtendComponentsByHTMLElement.includes(component))
                WrapperGS._ExtendComponentsByHTMLElement.push(component);
    }
    public static ReduceComponentsByHTMLElement(component: WrapperHTMLElementedConstructor) {
        remove(WrapperGS._ExtendComponentsByHTMLElement, component);
    }


    private static _ImplementWrapperByGSA: Map<string, WrapperDelegate> = new Map<string, WrapperDelegate>();
    /**
     * Call linked function for each wrapper who has specific dataArg
     * @param dataArg
     * @param func (wr:Wrapper affected, rootWr:Wrapper of template)
     */
    public static ImplementWrapperByGSA(gsa: string, func: WrapperDelegate) {
        WrapperGS._ImplementWrapperByGSA.set(gsa, func);
    }   

    private static _ImplementWrapperByClassArg: Map<string, WrapperDelegate> = new Map<string, WrapperDelegate>();
    /**
     * Call linked function for each wrapper who has specific dataArg
     * @param dataArg
     * @param func (wr:Wrapper affected, rootWr:Wrapper of template)
     */
    public static ImplementWrapperByClassArg(classArg: string, func: WrapperDelegate) {
        WrapperGS._ImplementWrapperByClassArg.set(classArg, func);
    }   

    public static get NameOfInstInPropOfEl() { return 'z_gswrapper_inst'; }
    public static IsWrapper(el: unknown): el is WrapperGS { return el instanceof WrapperGS }

    private static isHTMLElement(el: unknown): el is HTMLElement {
        if (!el || (el as Node).nodeType !== 1) return false;
        const element = el as Element;
        const htmlElementConstructor = element.ownerDocument?.defaultView?.HTMLElement;
        if (htmlElementConstructor) return element instanceof htmlElementConstructor;
        return !element.namespaceURI || element.namespaceURI === WrapperGS._htmlNamespace;
    }

    /**
     * Wrap element in WrapperGs
     * Происходит вокруг уже существующего элемента, поэтому мы можем инжектировать другие компоненты в отличии от конструктора
     * Где инжектировать нельзя кроме как через наследование
     * @param el
     */
    public static Wrap(el: WrapperGS): WrapperGS;
    public static Wrap(el: HTMLElement): WrapperGS;
    public static Wrap(el: unknown): WrapperGS | undefined;
    public static Wrap(el: unknown): WrapperGS | undefined {
        if (!el) return undefined;
        if (WrapperGS.IsWrapper(el)) return el;
        if (!WrapperGS.isHTMLElement(el)) return undefined;
        const cached = (el as any)[WrapperGS.NameOfInstInPropOfEl] as WrapperGS | undefined;
        if (cached) return cached;

        const htmlEl = el;
        //wrapping extendsByTagClass
        for (const key of WrapperGS._ExtendComponentsByTagClass.keys())
            if (htmlEl.classList.contains(key)) {
                const c = WrapperGS._ExtendComponentsByTagClass.get(key);
                if (c) return new c(htmlEl);
            }
        //wrapping extendsByHTMLElementValidationFunc
        for (const c of WrapperGS._ExtendComponentsByHTMLElement)
            if (c.ForHTMLElement(htmlEl)) return new c(htmlEl);  
        return new WrapperGS(htmlEl);
    }


    protected _controls?: Map<string, WrapperGS>;
    public getControl(name:string): WrapperGS | undefined {
        if (!this._controls) return undefined;
        return this._controls.get(name);
    }

    public readonly el: T;

    constructor(elOrTmplName: T|string, mode: boolean | WrapperInitModeGSEnum = WrapperInitModeGSEnum.Wrap)
    {
        let tmplWr: WrapperGS | undefined;
        let resolvedElement: T | undefined;
        const cloneElement = mode === true || mode === WrapperInitModeGSEnum.CloneElement;
        const explicitAdoption = mode === WrapperInitModeGSEnum.Adopt;
        let adopt = explicitAdoption;
        //getting template wrapper
        if (typeof elOrTmplName === 'string') {
            tmplWr = getTemplateWrapper(elOrTmplName);
        } else if (cloneElement) {
            tmplWr = WrapperGS.Wrap(elOrTmplName);
        } else {
            resolvedElement = elOrTmplName;
            adopt = adopt || (
                new.target !== WrapperGS
                && !!resolvedElement.getAttribute('gs-' + templateMetadataGS.templateName)
            );
        }
        //if there is tmpl
        if (tmplWr)
        {
            //getting template tag arguments
            const attrsMap = tmplWr.getAttrsObj();
            if (attrsMap.has('id')) attrsMap.delete('id');
            const attrs: Record<string, string> = {};
            for (const [key, value] of attrsMap) attrs[key] = value;
    
            //getting target tag
            let tagName = tmplWr.el.tagName.toLowerCase();
            const needSetTag = tmplWr.getGSA(templateMetadataGS.templateTag);
            if (needSetTag) tagName = needSetTag;
    
            //creating new Element
            if (tagName === "template") resolvedElement = div(attrs, tmplWr.html) as unknown as T;
            else resolvedElement = tag(tagName, attrs, tmplWr.html) as unknown as T;
        }

        if (!resolvedElement) throw new Error("WrapperGS could not resolve an element.");
        if (!WrapperGS.isHTMLElement(resolvedElement))
            throw new Error("WrapperGS supports HTMLElement instances only.");
        this.el = resolvedElement;

        const cached = (this.el as any)[WrapperGS.NameOfInstInPropOfEl] as WrapperGS | undefined;
        if (cached) {
            const requestedConstructor = new.target as unknown as Function;
            if (requestedConstructor.prototype.isPrototypeOf(cached)) return cached as any;
            const cachedName = cached.constructor.name || "unknown wrapper";
            const requestedName = requestedConstructor.name || "unknown wrapper";
            throw new Error(`Element is already wrapped by '${cachedName}' and cannot be wrapped by '${requestedName}'.`);
        }
        (this.el as any)[WrapperGS.NameOfInstInPropOfEl] = this;

        if (tmplWr) this._fillTmpl(false);
        else if (adopt) this._fillTmpl(true);

        ///Processing extends initDataArgs
        for (const key of WrapperGS._ImplementWrapperByGSA.keys()) {
            if (!this.getGSA(key)) continue;
            WrapperGS._ImplementWrapperByGSA.get(key)?.(this);
        }
        
        ///Processing extends initClassArgs
        this.el.classList.forEach((cl) => {
            if (!cl) return;
            WrapperGS._ImplementWrapperByClassArg.get(cl)?.(this);
        });
    }    

    private _fillTmpl(adopt: boolean)
    {
        if (adopt) {
            this._adoptTmpl();
            return;
        }

        const rootWr = this;
        this._controls = new Map<string, WrapperGS>()
        const rootCtrls = this._controls;
        const lang = getLang();
        const locArg = "loc-"+lang;
        const locPlaceholderArg = "loc-placeholder-"+lang;

        /**Fill element */
        const fillData = (wr: WrapperGS) =>
        {
            //If it's top-level template, remove it
            if (wr.el.tagName.toLowerCase() === "template") { wr.cut(); return; }

            const hasInjection = wr.el.hasAttribute('gs-ins');
            const gs_ins = wr.getGSA("ins").trim();
            if (hasInjection) {
                if (!gs_ins) {
                    const owner = rootWr.getGSA(templateMetadataGS.templateName)
                        || rootWr.constructor.name
                        || 'unknown';
                    throw new Error(`Required gs-ins in template '${owner}' must name a component or template.`);
                }
                const insConstructor = WrapperGS.GetInjectedComponentConstructor(gs_ins);
                let injected: WrapperGS;
                if (insConstructor) {
                    injected = new insConstructor();
                } else {
                    const template = tryGetTemplateWrapper(gs_ins);
                    if (!template) {
                        const owner = rootWr.getGSA(templateMetadataGS.templateName)
                            || rootWr.constructor.name
                            || 'unknown';
                        throw new Error(
                            `Required gs-ins '${gs_ins}' in template '${owner}' cannot be resolved. `
                            + 'Register its component constructor or include its template in the loaded GS template pack.'
                        );
                    }
                    injected = new WrapperGS(template.el, WrapperInitModeGSEnum.CloneElement);
                }

                wr.getAttrsObj().forEach((v,k)=>{if(k.toLowerCase()=="class") return; injected.attr(k,v)});
                wr.el.classList.forEach((cl)=>injected.addClass(cl));
                wr.replace(injected);

                //Text injection
                const txts = wr.q('text').all;
                //Text injection with deep ctrls like cmp1CtrlPage.cmp2CtrlBlock.cmp3CtrlText
                for(const txt of txts)
                {
                    const target = injected._searchGSTarget(txt.attr(templateMetadataGS.controlName));
                    if (target) target.text = txt.text;
                }
                //Text injection
                const attrs = wr.q('attr').all;
                //Text injection with deep ctrls like cmp1CtrlPage.cmp2CtrlBlock.cmp3CtrlText
                for(const attr of attrs)
                {
                    const target = injected._searchGSTarget(attr.attr(templateMetadataGS.controlName));
                    if (!target) continue;
                    const attrName = attr.attr("name");
                    if (!attrName) continue;
                    target.attr(attrName, attr.html);
                }

                injected.removeGSA("ins");
                injected.setGSA("ins-ed", gs_ins);
                fillData(injected);
                return;
            }

            let controlName: string | undefined = wr.getGSA(templateMetadataGS.controlName);
            if (wr === rootWr) controlName = undefined; //Контроль на сам шаблон может быть только во вложенности, а значит на root объекте его быть не может
            if (controlName) rootCtrls.set(controlName, wr);                        

            //Check - is it template
            const tmplName = wr.getGSA(templateMetadataGS.templateName);
            if (tmplName && !wr.getGSA('ins-ed'))
                //It's nested template
                if (rootWr !== wr) {
                    const constructFunc = getTemplateConstructor<WrapperGS>(tmplName) as WrapperConstructor | undefined;
                    if (!constructFunc) return;
                    const newW = new constructFunc();
                    wr.replace(newW);
                    if (controlName) {
                        rootCtrls.delete(controlName);
                        rootCtrls.set(controlName, newW);
                    }
                    return;
                }
            
            //Локализация
            const locText = wr.getGSA(locArg);
            if (locText) wr.text=locText;
            const locPlaceholder = wr.getGSA(locPlaceholderArg);
            if (locPlaceholder) (wr.el as any).placeholder=locPlaceholder;

            //Рекурсивный обход для не вложенных шаблонов
            wr.getChildren().map(fillData);        
        };        
        rootWr.q('*'); //преобертка, чтобы была последовательная инициализация
        fillData(rootWr);
        rootWr.q('template').cut();
    }

    private _adoptTmpl(): void {
        const rootCtrls = new Map<string, WrapperGS>();
        this._controls = rootCtrls;

        const visit = (element: Element): void => {
            const controlName = element.getAttribute('gs-' + templateMetadataGS.controlName) || '';
            const injectedName = element.getAttribute('gs-ins-ed') || '';
            const templateName = element.getAttribute('gs-' + templateMetadataGS.templateName) || '';
            const nestedName = injectedName || templateName;

            if (nestedName) {
                const constructor = WrapperGS.GetInjectedComponentConstructor(nestedName);
                if (constructor) {
                    const nested = new constructor(element as HTMLElement);
                    if (nested.el !== element)
                        throw new Error(`Template '${nestedName}' cannot adopt its existing element.`);
                    if (controlName) rootCtrls.set(controlName, nested);
                    return;
                }
            }

            const wrapper = WrapperGS.Wrap(element);
            if (!wrapper) return;
            if (controlName) rootCtrls.set(controlName, wrapper);
            Array.from(element.children).forEach(child => visit(child));
        };

        Array.from(this.el.children).forEach(child => visit(child));
    }


    q(selector: any) { return new GS(selector, this.el); }
    hasClass(className: string) { return this.el.classList?.contains(className) ?? false; }    
    addClass(className: string) { if (!this.el.classList) return; if (!this.hasClass(className)) this.el.classList.add(className); }
    addClasses(classesNames: string[]) { for (const c of classesNames) this.addClass(c); }    
    removeClass(className: string) { if (!this.el.classList) return; if (this.hasClass(className)) this.el.classList.remove(className); }    
    flipClass(className: string) { if (!this.el.classList) return; if (this.hasClass(className)) this.removeClass(className); else this.addClass(className); }

    private getElement(el: WrapperGS | Element): Element {
        if (WrapperGS.IsWrapper(el)) return (el as WrapperGS).el; else return el as Element;
    }

    /**Add after this wrapper*/
    after(el: WrapperGS | Element) { this.el.after(this.getElement(el)); }
    /**Add before this wrapper*/
    before(el: WrapperGS | Element) { this.el.before(this.getElement(el)); }
    /**Add to start of childrens*/
    prepend(el: WrapperGS | Element) { this.el.prepend(this.getElement(el)); }
    /**Add to end of childrens*/
    append(el: WrapperGS | Element) { this.el.append(this.getElement(el)); }
    /**Remove this wrapper */
    cut() { this.el.remove(); }
    /**
     * Replace this wrapper by another
     * @param {WrapperGS} w 
     */
    replace(by: WrapperGS | Element)
    {
        if (this.el.parentElement === null) return;
        const el = this.getElement(by);
        this.before(el);
        this.cut();        
    };

    get isEnabled() { return !this.hasClass(WrapperGS.ClassNameDisabled); }
    disable() { (this.el as any)[WrapperGS.ClassNameDisabled] = true; this.addClass(WrapperGS.ClassNameDisabled); };
    enable() { (this.el as any)[WrapperGS.ClassNameDisabled] = false; this.removeClass(WrapperGS.ClassNameDisabled); };
    enableFlip() { this.isEnabled ? this.disable() : this.enable(); };
    enableSet(value:boolean) { value ? this.enable() : this.disable(); };
   
    get isShowed() { return !this.hasClass(WrapperGS.ClassNameHide); }
    hide() { this.addClass(WrapperGS.ClassNameHide); };
    show() { this.removeClass(WrapperGS.ClassNameHide); };
    showFlip() { this.isShowed ? this.hide() : this.show(); };
    showSet(value:boolean) { value ? this.show() : this.hide(); };

    get isActive() { return this.hasClass(WrapperGS.ClassNameActive); }
    inactive() {this.removeClass(WrapperGS.ClassNameActive);this._actionOnTarget(false);};
    active() { this.addClass(WrapperGS.ClassNameActive);this._actionOnTarget(true);};
    activeFlip() { this.isActive ? this.inactive() : this.active(); };
    activeSet(value:boolean){ value ? this.active() : this.inactive(); }

    //поиск ближайшего контрола по подъему вверх а потом в глубь компонента
    private _searchGSTarget(gsTargetQuery: string | null | undefined): WrapperGS | undefined
    {
        if (!gsTargetQuery) return undefined;
        const stepsNames = gsTargetQuery.split('.');
        const firstStepName = stepsNames[0];
        if (!firstStepName) return undefined;
        //Search Up from down
        let wrStep: WrapperGS | undefined = this;
        while(true)
        {
            const target = wrStep.getControl(firstStepName);
            if (target) {wrStep = target; break;}
            if (!wrStep.el.parentElement) {wrStep = undefined; break;}
            wrStep = WrapperGS.Wrap(wrStep.el.parentElement);
        }
        if (!wrStep) return undefined;
        //Search deep in
        for(let i=1; i<stepsNames.length;i++)
        {
            const ctrlName = stepsNames[i];
            if (!ctrlName) return undefined;
            wrStep = wrStep.getControl(ctrlName);
            if (!wrStep) return undefined;
        }
        return wrStep;
    }
    //Поверка и выполнение действий по цели
    private _actionOnTarget(value:boolean)
    {
        const targetShow = this._searchGSTarget(this.getGSA(WrapperGS.AttrGSTargetShow));
        if (targetShow) targetShow.showSet(value);
        const targetActive = this._searchGSTarget(this.getGSA(WrapperGS.AttrGSTargetActive));
        if (targetActive) targetActive.activeSet(value);
    }

    scrollTo()
    {
        let e: HTMLElement | undefined = this.el, posX = 0, posY = 0;
        while (e) { posX += e.offsetLeft; posY += e.offsetTop; e = toType(e.offsetParent, HTMLElement); }
        RuntimeGS.window?.scrollTo(posX, posY);
    };     

    get text(): string { return this.el.innerText; }
    set text(value: string) {
        try {
            this.el.innerText = value;
        } catch {
            // Server DOM implementations may expose innerText as read-only.
            this.el.textContent = value;
        }
    }

    get html(): string { return this.el.innerHTML; }    
    set html(value: string) { this.el.innerHTML = value; }

    attr(name: string): string | null;
    attr(name: string, value: string | null): void;
    attr(name: string, value?: string | null): string | null | void {
        if (value === undefined) return this.el.getAttribute(name);
        if (value === null) this.el.removeAttribute(name);
        else this.el.setAttribute(name, value);
    }

    attrRemove(name: string) { this.attr(name,null) }

    /**
     * Return obj this attrs
     * */
    getAttrsObj(): Map<string, string>
    {
        const map: Map<string, string> = new Map();
        for (const attribute of Array.from(this.el.attributes))
            map.set(attribute.name, attribute.value);
        return map;
    }

    getData(name: string) { const d = this.attr('data-' + name); if (!d) return ''; else return d; }    
    removeData(name: string) { this.attr('data-' + name, null); }    
    setData(name: string, value: string) { this.attr('data-' + name, value); }

    getGSA(name: string) { const d = this.attr('gs-' + name); if (!d) return ''; else return d; }
    removeGSA(name: string) { this.attr('gs-' + name, null); }
    setGSA(name: string, value: string) { this.attr('gs-' + name, value); }

    /**Get children without deep */
    getChildren(): WrapperGS[] {

        const list: WrapperGS[] = [];
        this.el.childNodes.forEach(e => {
            const wr = WrapperGS.Wrap(e);
            if (wr) list.push(wr);
        });
        return list;
    }
    /**Get all children include children of children*/
    getAllChildren(): WrapperGS[]
    {
        
        const list: WrapperGS[] = [];
        function searchChildren(el: Node)
        {
            el.childNodes.forEach(e =>
            {
                const wr = WrapperGS.Wrap(e);
                if (wr) list.push(wr);
                searchChildren(e);                
            });    
        }
        searchChildren(this.el);        
        return list;        
    }
    set onclick(value: ((this: GlobalEventHandlers, ev: MouseEvent) => any)) { this.el.onclick = value; }
}


WrapperGS.ImplementWrapperByGSA('set_src_after', (wr) => {
    if (wr.el.tagName.toLowerCase() === 'img')
        (wr.el as HTMLImageElement).src = wr.getGSA('set_src_after');
});

WrapperGS.ImplementWrapperByGSA('scroll_after_focusin', (wr) => {
    const scrollHandler = function (e: Event){
        const el = e.target as Element;
        if (el?.scrollIntoView) el.scrollIntoView();
    }    
    wr.el.addEventListener('focusin', scrollHandler);
});
